import type { Reducer } from 'redux'

export const SELECT_TARIFF_DIALOG = 'SELECT_TARIFF_DIALOG'
export const SELECT_TARIFF = 'SELECT_TARIFF'
export const SELECT_TARIFF_LOADING = 'SELECT_TARIFF_LOADING'
export const SELECT_TARIFF_FAILURE = 'SELECT_TARIFF_FAILURE'
export const SELECT_TARIFF_SUCCESS = 'SELECT_TARIFF_SUCCESS'

export const setSelectTariffDialog = (
  isOpen: boolean,
  projectId: number | string | null | undefined,
  countryId: number | string | null | undefined,
  currentOrProposed: 'current' | 'proposed' | null | undefined,
  deactivateSceneOnOpen = true,
  zip: string | null = null
) => ({
  type: SELECT_TARIFF_DIALOG,
  payload: {
    isOpen,
    projectId,
    countryId,
    currentOrProposed,
    deactivateSceneOnOpen,
    zip,
  },
})

export const selectTariff = (
  orgId: unknown,
  projectId: unknown,
  utilityTariffOrgId: unknown,
  utilityTariffId: unknown,
  currentOrProposed: 'current' | 'proposed'
) => ({
  type: SELECT_TARIFF,
  payload: {
    orgId,
    projectId,
    url: 'projects/' + projectId + '/',
    data: {
      ['utility_tariff_' + currentOrProposed]: utilityTariffId
        ? window.API_BASE_URL +
          'orgs/' +
          utilityTariffOrgId +
          '/utility_tariffs/' +
          utilityTariffId +
          '/'
        : null,
    },
  },
  meta: { resource: 'projects', fetch: 'CUSTOM_PUT', cancelPrevious: false },
})

type RouterLocationChangeAction = {
  type: '@@router/LOCATION_CHANGE'
  payload: { location: { pathname: string } }
}

type SelectTariffDialogAction = {
  type: typeof SELECT_TARIFF_DIALOG
  payload: {
    isOpen: boolean
    projectId?: number | string | null
    countryId?: number | string | null
    currentOrProposed?: 'current' | 'proposed' | null
    deactivateSceneOnOpen?: boolean
    zip?: string | null
  }
}

type NarrowAction =
  | RouterLocationChangeAction
  | SelectTariffDialogAction
  | { type: typeof SELECT_TARIFF_SUCCESS }
  | { type: typeof SELECT_TARIFF_LOADING }
  | { type: typeof SELECT_TARIFF_FAILURE }
  | { type: string; payload?: Record<string, unknown> }

/** Mutable bag used by tariff dialog + saga status; intentionally loose vs store slice typing. */
const selectTariffReducer: Reducer<Record<string, unknown>> = (
  previousState = {},
  action: NarrowAction | { type: string; payload?: Record<string, unknown> }
): Record<string, unknown> => {
  let next = previousState

  if (action.type === '@@router/LOCATION_CHANGE') {
    const locationChangePayload = (
      action as RouterLocationChangeAction
    ).payload
    const availablePath = ['/design', '/studio', '/energy', '/projects']
    if (
      !availablePath.some((path) =>
        locationChangePayload.location.pathname.includes(path)
      )
    ) {
      next = { ...next, isOpen: false }
    }
  }

  if (action.type === SELECT_TARIFF_DIALOG) {
    const dialogPayload = (
      action as SelectTariffDialogAction
    ).payload
    const version = next && next.version ? Number(next.version) + 1 : 1

    if (dialogPayload.deactivateSceneOnOpen) {
      try {
        if (dialogPayload.isOpen === true) {
          const enablePluginsOverride = true
          // Studio globals (not on Window type)
          const win = window as typeof window & {
            editor?: { interactive: (...args: unknown[]) => void }
            MapHelper?: { interactive: (interactive: boolean) => void }
          }
          win.editor && win.editor.interactive(false, undefined, enablePluginsOverride)
          win.MapHelper && win.MapHelper.interactive(false)
        } else {
          const win = window as typeof window & {
            editor?: { interactive: (interactive: boolean) => void }
            MapHelper?: { interactive: (interactive: boolean) => void }
          }
          win.editor && win.editor.interactive(true)
          win.MapHelper && win.MapHelper.interactive(true)
        }
      } catch {}
    }

    next = {
      ...next,
      ...{
        isOpen: dialogPayload.isOpen,
        projectId: dialogPayload.projectId,
        countryId: dialogPayload.countryId,
        currentOrProposed: dialogPayload.currentOrProposed,
        zip: dialogPayload.zip,
        version,
      },
    }
  }

  if (action.type === SELECT_TARIFF_SUCCESS) {
    next = { status: 'success' }
  }

  if (action.type === SELECT_TARIFF_LOADING) {
    next = { status: 'loading' }
  }

  if (action.type === SELECT_TARIFF_FAILURE) {
    next = { status: 'failure' }
  }

  return next
}

export default selectTariffReducer
